import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, RefreshCw, Key, Webhook, Plug, AlertCircle, Send } from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

export default function IntegrationsPanel() {
  const { showSuccess, showError, showInfo } = useAlert();
  const [activeSubTab, setActiveSubTab] = useState('keys');
  const [summary, setSummary] = useState(null);

  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [availableConnectors, setAvailableConnectors] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, k, w, c, ac, we] = await Promise.all([
        axios.get(`${API}/mrv/integrations/summary`),
        axios.get(`${API}/mrv/integrations/api-keys`),
        axios.get(`${API}/mrv/integrations/webhooks`),
        axios.get(`${API}/mrv/integrations/connectors`),
        axios.get(`${API}/mrv/integrations/connectors/available`),
        axios.get(`${API}/mrv/integrations/webhooks/events`),
      ]);
      setSummary(s.data);
      setKeys(k.data.api_keys || []);
      setWebhooks(w.data.webhooks || []);
      setConnectors(c.data.connectors || []);
      setAvailableConnectors(ac.data.connectors || []);
      setWebhookEvents(we.data.events || []);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const SUB_TABS = [
    { id: 'keys',       label: 'API Keys',  Icon: Key },
    { id: 'webhooks',   label: 'Webhooks',  Icon: Webhook },
    { id: 'connectors', label: 'Connectors', Icon: Plug },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Integrations</h2>
        <button onClick={fetchAll} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Active API keys" value={summary?.active_api_keys ?? '—'} accent="blue" />
        <Stat label="Active webhooks" value={summary?.active_webhooks ?? '—'} accent="emerald" />
        <Stat label="Active connectors" value={summary?.active_connectors ?? '—'} accent="violet" />
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200 mb-3">
        <nav className="-mb-px flex gap-2">
          {SUB_TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => { setActiveSubTab(id); setShowCreate(false); setCreatedKey(null); setCreatedSecret(null); }}
                    className={`py-2 px-3 inline-flex items-center gap-1 border-b-2 text-sm font-medium ${
                      activeSubTab === id
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Newly issued plaintext warning */}
      {createdKey && (
        <PlaintextNotice
          title="New API key — copy it now, it won't be shown again"
          value={createdKey}
          onDismiss={() => setCreatedKey(null)}
        />
      )}
      {createdSecret && (
        <PlaintextNotice
          title="Webhook secret — copy it now, it won't be shown again"
          value={createdSecret}
          onDismiss={() => setCreatedSecret(null)}
        />
      )}

      {/* API Keys */}
      {activeSubTab === 'keys' && (
        <ApiKeysSection
          keys={keys}
          showCreate={showCreate}
          onShowCreate={(v) => setShowCreate(v)}
          onCreated={(plaintext) => { setShowCreate(false); setCreatedKey(plaintext); fetchAll(); }}
          onChanged={fetchAll}
        />
      )}

      {/* Webhooks */}
      {activeSubTab === 'webhooks' && (
        <WebhooksSection
          webhooks={webhooks}
          events={webhookEvents}
          showCreate={showCreate}
          onShowCreate={(v) => setShowCreate(v)}
          onCreated={(secret) => { setShowCreate(false); setCreatedSecret(secret); fetchAll(); }}
          onChanged={fetchAll}
        />
      )}

      {/* Connectors */}
      {activeSubTab === 'connectors' && (
        <ConnectorsSection
          connectors={connectors}
          available={availableConnectors}
          showCreate={showCreate}
          onShowCreate={(v) => setShowCreate(v)}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'gray' }) {
  const map = {
    gray:    'bg-gray-50 text-gray-700',
    blue:    'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    violet:  'bg-violet-50 text-violet-700',
  };
  return (
    <div className={`p-3 rounded border border-gray-200 ${map[accent]}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function PlaintextNotice({ title, value, onDismiss }) {
  return (
    <div className="mb-3 p-3 border-2 border-amber-300 bg-amber-50 rounded">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-semibold text-amber-900 flex items-center gap-1 mb-1">
            <AlertCircle className="w-4 h-4" /> {title}
          </div>
          <code className="block px-2 py-1.5 bg-white rounded font-mono text-xs break-all">{value}</code>
        </div>
        <button onClick={onDismiss} className="text-amber-700 hover:text-amber-900 text-xs">
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ---------- API Keys ----------

function ApiKeysSection({ keys, showCreate, onShowCreate, onCreated, onChanged }) {
  const { showSuccess, showError } = useAlert();

  const revoke = async (id) => {
    if (!window.confirm('Revoke this API key? This is irreversible.')) return;
    try {
      await axios.delete(`${API}/mrv/integrations/api-keys/${id}`);
      showSuccess('Key revoked');
      onChanged();
    } catch (e) {
      showError(e.response?.data?.detail || 'Revoke failed');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button onClick={() => onShowCreate(true)}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New API key
        </button>
      </div>

      {showCreate && <CreateApiKeyForm onCreated={onCreated} onCancel={() => onShowCreate(false)} />}

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Name</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Prefix</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Status</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Last used</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Created</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {keys.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No API keys yet</td></tr>
            )}
            {keys.map(k => (
              <tr key={k.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{k.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}…</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${k.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {k.is_active ? 'Active' : 'Revoked'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {k.is_active && (
                    <button onClick={() => revoke(k.id)} className="text-red-500 hover:text-red-700 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateApiKeyForm({ onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('mrv:read');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name) { showError('Name required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/mrv/integrations/api-keys`, {
        name,
        allowed_scopes: scopes.split(',').map(s => s.trim()).filter(Boolean),
      });
      showSuccess('Key created — copy plaintext now');
      onCreated(res.data.key);
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 p-3 border border-emerald-200 bg-emerald-50 rounded">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder="Key name" value={name}
               onChange={e => setName(e.target.value)}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Scopes (comma-separated)" value={scopes}
               onChange={e => setScopes(e.target.value)}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// ---------- Webhooks ----------

function WebhooksSection({ webhooks, events, showCreate, onShowCreate, onCreated, onChanged }) {
  const { showSuccess, showError, showInfo } = useAlert();

  const test = async (id) => {
    try {
      const res = await axios.post(`${API}/mrv/integrations/webhooks/${id}/test`);
      showInfo(`Webhook test logged (status: ${res.data.status})`);
    } catch (e) {
      showError(e.response?.data?.detail || 'Test failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    try {
      await axios.delete(`${API}/mrv/integrations/webhooks/${id}`);
      showSuccess('Webhook deleted');
      onChanged();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button onClick={() => onShowCreate(true)}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New webhook
        </button>
      </div>

      {showCreate && <CreateWebhookForm events={events} onCreated={onCreated} onCancel={() => onShowCreate(false)} />}

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Name</th>
              <th className="px-3 py-2 font-semibold text-gray-700">URL</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Events</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Active</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {webhooks.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No webhooks yet</td></tr>
            )}
            {webhooks.map(w => (
              <tr key={w.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{w.name}</td>
                <td className="px-3 py-2 font-mono text-xs truncate max-w-[200px]" title={w.target_url}>
                  {w.target_url}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {(w.events || []).slice(0, 3).join(', ')}{(w.events || []).length > 3 ? '…' : ''}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${w.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                    {w.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 flex gap-1 justify-end">
                  <button onClick={() => test(w.id)} className="text-blue-600 hover:text-blue-800 p-1" title="Send test">
                    <Send className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(w.id)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateWebhookForm({ events, onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({ name: '', target_url: '', events: [] });
  const [saving, setSaving] = useState(false);

  const toggleEvent = (e) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(e) ? f.events.filter(x => x !== e) : [...f.events, e],
    }));
  };

  const submit = async () => {
    if (!form.name) { showError('Name required'); return; }
    if (!form.target_url) { showError('Target URL required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/mrv/integrations/webhooks`, form);
      showSuccess('Webhook created — copy secret now');
      onCreated(res.data.secret);
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 p-3 border border-emerald-200 bg-emerald-50 rounded">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input placeholder="Webhook name" value={form.name}
               onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="https://example.com/webhook" value={form.target_url}
               onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="text-xs font-semibold text-gray-700 mb-1">Events to subscribe to:</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {events.map(ev => (
          <label key={ev} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white border border-gray-300 rounded cursor-pointer">
            <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} />
            {ev}
          </label>
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// ---------- Connectors ----------

function ConnectorsSection({ connectors, available, showCreate, onShowCreate, onCreated, onChanged }) {
  const { showSuccess, showError } = useAlert();

  const sync = async (id) => {
    try {
      const res = await axios.post(`${API}/mrv/integrations/connectors/${id}/sync`);
      showSuccess(`Synced — ${res.data.rows_synced || 0} rows`);
      onChanged();
    } catch (e) {
      showError(e.response?.data?.detail || 'Sync failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this connector?')) return;
    try {
      await axios.delete(`${API}/mrv/integrations/connectors/${id}`);
      showSuccess('Connector deleted');
      onChanged();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button onClick={() => onShowCreate(true)}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New connector
        </button>
      </div>

      {showCreate && (
        <CreateConnectorForm available={available} onCreated={onCreated} onCancel={() => onShowCreate(false)} />
      )}

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Name</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Type</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Last sync</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Active</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {connectors.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No connectors yet</td></tr>
            )}
            {connectors.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-gray-600">{c.connector_type}</td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : 'Never'}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 flex gap-1 justify-end">
                  <button onClick={() => sync(c.id)} className="text-blue-600 hover:text-blue-800 p-1" title="Sync">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateConnectorForm({ available, onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({ name: '', connector_type: available[0]?.type || 'verra', config: '{}' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name) { showError('Name required'); return; }
    let cfg = {};
    try { cfg = JSON.parse(form.config || '{}'); } catch { showError('Config must be valid JSON'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/integrations/connectors`, {
        name: form.name,
        connector_type: form.connector_type,
        config: cfg,
        is_active: true,
      });
      showSuccess('Connector created');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 p-3 border border-emerald-200 bg-emerald-50 rounded">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <input placeholder="Connector name" value={form.name}
               onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <select value={form.connector_type}
                onChange={e => setForm(f => ({ ...f, connector_type: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {available.map(c => <option key={c.type} value={c.type}>{c.name}</option>)}
        </select>
      </div>
      <textarea
        placeholder="Config JSON (e.g. {&quot;api_key&quot;: &quot;...&quot;, &quot;endpoint&quot;: &quot;...&quot;})"
        value={form.config}
        onChange={e => setForm(f => ({ ...f, config: e.target.value }))}
        rows={3}
        className="w-full px-2 py-1 text-xs font-mono border rounded mb-2"
      />
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
